<template>
  <header class="app-header">
    <div class="header-content">
      <!-- Bouton menu mobile -->
      <button
        @click="toggleSidebar"
        class="mobile-menu-btn lg:hidden"
        aria-label="Toggle menu"
      >
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      <!-- Titre de la page -->
      <div class="page-title">
        <h1 class="title">{{ pageTitle }}</h1>
        <p class="subtitle" v-if="pageSubtitle">{{ pageSubtitle }}</p>
      </div>

      <!-- Actions header -->
      <div class="header-actions">
        <!-- Notifications -->
        <button 
          class="icon-btn" 
          aria-label="Notifications"
          @click="handleNotificationClick"
          :title="notificationPermissionText"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
        </button>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { requestNotificationPermission, showNotification } from '@/utils/notifications'

const emit = defineEmits<{
  toggleSidebar: []
}>()

const route = useRoute()

// État de la permission de notification
const notificationPermission = ref<NotificationPermission>('default')

onMounted(() => {
  // Vérifier l'état actuel de la permission (lecture seule, pas de demande)
  if ('Notification' in window) {
    notificationPermission.value = Notification.permission
  }
})

const notificationPermissionText = computed(() => {
  switch (notificationPermission.value) {
    case 'granted':
      return 'Notifications activées'
    case 'denied':
      return 'Notifications désactivées - Cliquez pour activer dans les paramètres'
    default:
      return 'Cliquez pour activer les notifications'
  }
})

// Gérer le clic sur le bouton de notification
const handleNotificationClick = () => {
  if (!('Notification' in window)) {
    showNotification({
      message: 'Les notifications ne sont pas supportées par ce navigateur',
      type: 'warning'
    })
    return
  }

  if (Notification.permission === 'granted') {
    // Permission déjà accordée, afficher un message
    showNotification({
      message: 'Les notifications sont déjà activées',
      type: 'info'
    })
    return
  }

  if (Notification.permission === 'denied') {
    // Permission refusée, informer l'utilisateur
    showNotification({
      message: 'Les notifications sont désactivées. Veuillez les activer dans les paramètres de votre navigateur.',
      type: 'warning',
      duration: 8000
    })
    return
  }

  // Demander la permission depuis le gestionnaire d'événements utilisateur (clic)
  requestNotificationPermission((permission) => {
    notificationPermission.value = permission
    if (permission === 'granted') {
      showNotification({
        message: 'Notifications activées avec succès',
        type: 'success'
      })
    } else {
      showNotification({
        message: 'Permission de notification refusée',
        type: 'warning'
      })
    }
  })
}

const pageTitle = computed(() => {
  const titleMap: Record<string, string> = {
    Dashboard: 'Dashboard',
    Bandes: 'Bandes & Productions',
    Finances: 'Finances & Comptabilité',
    Stocks: 'Gestion des Stocks',
    RH: 'Ressources Humaines',
    CRM: 'CRM & Ventes',
  }

  return titleMap[route.name as string] || 'AGRO NOVAMED'
})

const pageSubtitle = computed(() => {
  const subtitleMap: Record<string, string> = {
    Dashboard: 'Vue d\'ensemble de votre exploitation',
    Bandes: 'Gestion des bandes et productions',
    Finances: 'Suivi financier et comptable',
    Stocks: 'Inventaire et gestion des stocks',
    RH: 'Gestion du personnel',
  }

  return subtitleMap[route.name as string] || ''
})

const toggleSidebar = () => {
  emit('toggleSidebar')
}
</script>

<style scoped>
.app-header {
  @apply bg-white border-b border-gray-200 sticky top-0 z-30;
}

.header-content {
  @apply flex items-center gap-4 px-4 lg:px-6 py-4;
}

.mobile-menu-btn {
  @apply p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors;
}

.page-title {
  @apply flex-1;
}

.title {
  @apply text-xl font-bold;
  color: #047857;
}

.subtitle {
  @apply text-sm mt-0.5;
  color: #059669;
}

.header-actions {
  @apply flex items-center gap-2;
}

.icon-btn {
  @apply p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors;
}
</style>

